<template>
  <div class="precision-form-item__account">
    <UIDropdownSearchList
      v-if="isDropdownVisible"
      :data="listValues"
      placement="bottom-start"
      @click:item="handleAddOwner"
    >
      <template #default="{ show }">
        <UIButton
          label="Select owner"
          size="sm"
          variant="gray"
          fill="outlined"
          :trailing="ChevronDownIcon"
          @click="show"
        />
      </template>
    </UIDropdownSearchList>
    <span v-else>{{ displayedPath }}</span>
  </div>
  <div class="precision-form-item__amount">
    <UIInputPercent v-model="amount" size="sm" @ready="handleReady" />
  </div>
  <div class="precision-form-item__asset">
    <span>{{ assetName }}</span>
    <UIButton
      v-if="isJustCreated"
      v-tooltip.top="'Delete owner'"
      variant="danger"
      fill="light"
      size="sm"
      :leading="TrashIcon"
      @click="handleClickDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { DropdownListData } from '@types'
import { OrganizeFamilyStructurePath } from '../utils/types'

import { ASSET_FIELD } from '@/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIInputPercent } from '@ui/fields'
import { UIButton } from '@ui/buttons'
import { ChevronDownIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { UIDropdownSearchList } from '@ui/dropdowns'

type Props = {
  index: number
  item: OrganizeFamilyStructurePath
  listValues: DropdownListData[]
  isJustCreated: boolean
  assetName: string
}

type Emits = {
  update: [index: number, data: OrganizeFamilyStructurePath]
  delete: [index: number]
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()

const path = computed({
  get: () => JSON.stringify(props.item.path),
  set: (value: string) => {
    emit('update', props.index, { path: JSON.parse(value), amount: props.item.amount })
  },
})

const amount = computed({
  get: () => props.item.amount,
  set: (value: number) => {
    emit('update', props.index, { path: props.item.path, amount: value })
  },
})

const currentPath = computed(() => props.item.path.slice(1, -1))
const displayedPath = computed(() => {
  return currentPath.value.map(id => assetsBunchStore.getElementById(id)?.field<string>(ASSET_FIELD.NAME).value).join(' -> ')
})

const isDropdownVisible = computed(() => !currentPath.value.length)

const handleReady = (el: HTMLInputElement) => {
  if (props.index !== 0) return
  el.focus()
}

const handleAddOwner = (data?: string) => {
  if (!data) return
  path.value = data
}

const handleClickDelete = () => {
  emit('delete', props.index)
}
</script>

<style scoped>
.precision-form-item {
  &__account,
  &__amount,
  &__asset {
    @apply flex items-center py-1;
    @apply overflow-hidden;
  }

  &__account {

    > span {
      @apply text-xs font-medium text-right;
      @apply truncate;
    }

    .v-popper,
    .ui-button {
      @apply w-full;
    }

    :deep(.ui-button__label) {
      @apply flex-auto;
      @apply text-left;
    }
  }

  &__asset {
    @apply justify-between;

    > span {
      @apply text-xs font-medium;
      @apply truncate;
    }
  }

  &__amount {
    @apply gap-x-0.5;
  }
}
</style>
